import { getBackendUrl } from '@/lib/backendUrl';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle2, AlertCircle, KeyRound } from 'lucide-react';

const API = `${getBackendUrl()}/api`;

/**
 * Fallback activation page for recipients whose email was blocked or whose
 * invite link was stripped by their IT: admin shares the 8-character
 * activation code via another channel (phone/SMS/WhatsApp), user types it in
 * here alongside their email to set a password.
 */
export default function InviteActivatePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('enter'); // enter | ready | accepting | done
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const normaliseCode = (raw) =>
    (raw || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();

  const verify = async (e) => {
    e.preventDefault();
    setError('');
    const normCode = normaliseCode(code);
    if (!email || normCode.length < 6) {
      setError('Please enter your email and the 8-character activation code.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/invitations/validate-code`, {
        email: email.trim().toLowerCase(),
        activation_code: normCode,
      });
      setUser(data);
      setStep('ready');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Invalid email or activation code.');
    } finally {
      setLoading(false);
    }
  };

  const activate = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setStep('accepting');
    try {
      await axios.post(`${API}/invitations/accept`, {
        email: email.trim().toLowerCase(),
        activation_code: normaliseCode(code),
        password,
      });
      setStep('done');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not activate account.');
      setStep('ready');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="activate-page">
      <Card className="w-full max-w-md shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-violet-600" /> Activate with code
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 'enter' && (
            <form onSubmit={verify} className="space-y-4" data-testid="activate-verify-form">
              <p className="text-sm text-muted-foreground">
                If your invitation email didn't arrive or the link was blocked, enter the
                8-character activation code your administrator shared with you.
              </p>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="activate-email-input" autoComplete="email" required />
              </div>
              <div>
                <Label htmlFor="code">Activation code</Label>
                <Input id="code" value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="e.g. K4P9R2XM"
                  className="font-mono tracking-widest uppercase"
                  data-testid="activate-code-input" required />
                <p className="text-xs text-muted-foreground mt-1">Letters and numbers — dashes and spaces are ignored.</p>
              </div>
              {error && (
                <div className="flex items-start gap-2 text-sm text-red-600" data-testid="activate-error">
                  <AlertCircle className="h-4 w-4 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              <Button type="submit" className="w-full" disabled={loading} data-testid="activate-verify-btn">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Continue
              </Button>
            </form>
          )}

          {(step === 'ready' || step === 'accepting') && user && (
            <form onSubmit={activate} className="space-y-4" data-testid="activate-password-form">
              <div className="text-sm">
                <p className="text-muted-foreground">Signing up as</p>
                <p className="font-medium">{user.first_name} {user.last_name}</p>
                <p className="text-xs text-muted-foreground">{user.email}</p>
              </div>
              <div>
                <Label htmlFor="new-password">New password</Label>
                <Input id="new-password" type="password" minLength={8}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="activate-password-input" autoComplete="new-password" required />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input id="confirm-password" type="password" minLength={8}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="activate-confirm-input" autoComplete="new-password" required />
              </div>
              {error && (
                <div className="text-sm text-red-600" data-testid="activate-inline-error">{error}</div>
              )}
              <Button type="submit" className="w-full" disabled={step === 'accepting'} data-testid="activate-submit-btn">
                {step === 'accepting' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Activate account
              </Button>
            </form>
          )}

          {step === 'done' && (
            <div className="text-center space-y-3 py-4" data-testid="activate-done">
              <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
              <p className="font-medium">Account activated</p>
              <p className="text-sm text-muted-foreground">You can now sign in with your new password.</p>
              <Button onClick={() => navigate('/login')} data-testid="activate-go-login-btn">Go to sign in</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
